import React, { useMemo, useEffect, useState } from 'react';
import PageTitle from '../layouts/PageTitle';
import { useTable, useGlobalFilter, useFilters, usePagination } from 'react-table';
import { ColumnFilter } from '../components/table/FilteringTable/ColumnFilter';
import { Button } from 'react-bootstrap';
import '../components/table/FilteringTable/filtering.css';
import DataTable from '../pages/commonPages/DataTable';
import CustomTooltip from '../../utils/CommonUtils';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import {
  useGetWorkDaysList,
  useGetWorkDaysDetail,
  useCreateWorkdays,
  useWorkDaysUpdate,
} from '../../actions/timesheet-actions';

export const WorkDaysListView = () => {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentWorkDayId, setCurrentWorkDayId] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    day: '',
    description: '',
    sort: ''
  });

  // API hooks
  const { data: workDaysList, isLoading: loadingWorkDays, refetch: refetchWorkDays } = useGetWorkDaysList();
  const { data: workDayDetail, refetch: fetchWorkDayDetail, isFetching: isFetchingDetails } = useGetWorkDaysDetail(currentWorkDayId);
  const { mutate: createWorkDay, isLoading: creatingWorkDay } = useCreateWorkdays();
  const { mutate: updateWorkDay, isLoading: updatingWorkDay } = useWorkDaysUpdate();

  // Days options for dropdown
  const daysOptions = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 
    'Thursday', 'Friday', 'Saturday'
  ];

  useEffect(() => {
    if (workDaysList) {
      setData(workDaysList);
    }
  }, [workDaysList]);

  // Fetch work day details when currentWorkDayId changes
  useEffect(() => {
    if (currentWorkDayId && isEditMode) {
      setIsLoadingDetails(true);
      fetchWorkDayDetail().finally(() => {
        setIsLoadingDetails(false);
      });
    }
  }, [currentWorkDayId, isEditMode]);

  // Update form data when work day details are fetched
  useEffect(() => {
    if (workDayDetail && isEditMode && !isFetchingDetails) {
      setFormData({
        day: workDayDetail.day,
        description: workDayDetail.description || '',
        sort: workDayDetail.sort.toString()
      });
    }
  }, [workDayDetail, isEditMode, isFetchingDetails]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form and modal state
  const resetForm = () => {
    setFormData({
      day: '',
      description: '',
      sort: ''
    });
    setCurrentWorkDayId(null);
    setIsEditMode(false);
    setIsLoadingDetails(false);
  };

  // Open modal for creating new work day
  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  // Open modal for editing existing work day
  const handleEdit = (id) => {
    setCurrentWorkDayId(id);
    setIsEditMode(true);
    setShowModal(true);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const payload = {
      day: formData.day,
      description: formData.description,
      sort: parseInt(formData.sort)
    };

    if (isEditMode && currentWorkDayId) {
      updateWorkDay({ id: currentWorkDayId, payload }, {
        onSuccess: () => {
          refetchWorkDays();
          setShowModal(false);
          resetForm();
        },
        onError: (error) => {
          console.error('Update failed:', error);
        }
      });
    } else {
      createWorkDay(payload, {
        onSuccess: () => {
          refetchWorkDays();
          setShowModal(false);
          resetForm();
        },
        onError: (error) => {
          console.error('Create failed:', error);
        }
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Day",
        Footer: "Day",
        accessor: "day",
        Filter: ColumnFilter,
        className: "minWidth200",
      },
      {
        Header: "Description",
        Footer: "Description",
        accessor: "description",
        Filter: ColumnFilter,
        className: "minWidth200",
      },
      {
        Header: "Sort Order",
        Footer: "Sort Order",
        accessor: "sort",
        Filter: ColumnFilter,
        className: "minWidth100",
      },
      {
        Header: "Action",
        Footer: "Action",
        accessor: "action",
        Filter: ColumnFilter,
        disableFilters: true,
        className: "minWidth100",
        Cell: ({ row }) => (
          <>
            <CustomTooltip id={`edit-${row.index}`} message="Edit">
              <Button 
                className="shadow btn-xs sharp me-1" 
                variant='primary light' 
                style={{ cursor: "pointer" }} 
                onClick={() => handleEdit(row.original.id)}
              >
                <i className="fas fa-pencil-alt"></i>
              </Button>
            </CustomTooltip>
          </>
        )
      },
    ],
    []
  );

  const tableInstance = useTable({
    columns,
    data,
    initialState: { pageIndex: 0 }
  }, useFilters, useGlobalFilter, usePagination);

  return (
    <>
      <PageTitle activeMenu="Work Days" motherMenu="Settings" />
      <div className="card">
        <div className="card-header">
          <h4 className="card-title"><i className="fa fa-calendar" aria-hidden="true"></i> Work Days</h4>
          <div className="d-inline-flex">
            <Button 
              className='me-2' 
              variant='primary btn-sm'
              onClick={handleAdd}
            >
              <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; Add Work Day
            </Button>
          </div>
        </div>
        <div className="card-body">
          <DataTable
            isLoading={loadingWorkDays}
            columns={columns}
            tableInstance={tableInstance}
          />
        </div>
      </div>

      {/* Modal for Add/Edit Work Day */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        resetForm();
      }}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit Work Day' : 'Add Work Day'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {isEditMode && isLoadingDetails ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading work day details...</p>
              </div>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Day <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="select"
                    name="day"
                    value={formData.day}
                    onChange={handleInputChange}
                    required
                    disabled={isEditMode}
                  >
                    <option value="">Select a day</option>
                    {daysOptions.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </Form.Control>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Sort Order <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    name="sort"
                    value={formData.sort}
                    onChange={handleInputChange}
                    required
                    min="1"
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowModal(false);
              resetForm();
            }}>
              Close
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={creatingWorkDay || updatingWorkDay || (isEditMode && isLoadingDetails)}
            >
              {creatingWorkDay || updatingWorkDay ? 'Saving...' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default WorkDaysListView;